/**
 * Feature flag (CUR-346): default the Workload Manager to the "My work" view (the user's own tasks and
 * the emails on claims/complaints they own) with a "See all" toggle, and apply the v1 visibility rules
 * for unmatched routed emails (org admin only). Off by default in every environment.
 */
export const ENABLE_WORKLOAD_MY_WORK_DEFAULT_FLAG = 'enable-workload-my-work-default';

// Unified status constants for all workload manager items
export const UNIFIED_WORKLOAD_STATUSES = {
    OVERDUE: 'overdue',
    DUE_SOON: 'due_soon',
    NOT_YET_DUE: 'not_yet_due',
} as const;

export const UNIFIED_WORKLOAD_STATUS_LABELS = {
    overdue: 'Overdue',
    due_soon: 'Due soon',
    not_yet_due: 'Not yet due',
} as const;

// For backwards compatibility, we also provide the completion statuses
export const UNIFIED_COMPLETION_STATUSES = {
    COMPLETED: 'completed',
    NOT_COMPLETED: 'not_completed',
} as const;

export const UNIFIED_COMPLETION_STATUS_LABELS = {
    completed: 'Completed',
    not_completed: 'Not completed',
} as const;

// Combined statuses for dropdowns that need both due date and completion statuses
export const COMBINED_WORKLOAD_STATUSES = {
    ...UNIFIED_WORKLOAD_STATUSES,
    ...UNIFIED_COMPLETION_STATUSES,
} as const;

export const COMBINED_WORKLOAD_STATUS_LABELS = {
    ...UNIFIED_WORKLOAD_STATUS_LABELS,
    ...UNIFIED_COMPLETION_STATUS_LABELS,
} as const;

// Unified work item types for workload manager
export const WORK_ITEM_TYPES = {
    TASK: 'task',
    CONTROL_ACTIVITY: 'control_activity',
    CONTROL_TEST: 'control_test',
    RISK_ACTION: 'risk_action',
    MSP_GOVERNANCE_CHECK: 'msp_governance_check',
} as const;

export const WORK_ITEM_TYPE_LABELS = {
    task: 'Task',
    control_activity: 'Control activity',
    control_test: 'Control test',
    risk_action: 'Risk action',
    msp_governance_check: 'Material Service Provider Governance check',
} as const;
// Array of work item types for easy iteration
export const WORK_ITEM_TYPES_ARRAY = Object.values(WORK_ITEM_TYPES);

/** Type guard: true iff `v` is one of the WORK_ITEM_TYPES values. */
export const isWorkItemType = (v: string): v is WorkItemType => WORK_ITEM_TYPES_ARRAY.some((type) => type === v);

// Display labels for parent record types from the workload SQL view (snake_case keys)
export const WORKLOAD_RECORD_TYPE_LABELS = {
    claim: 'Claim',
    complaint: 'Complaint',
    incident: 'Incident',
    obligation: 'Obligation',
    control: 'Control',
    risk: 'Risk',
    msp_provider: 'Material Service Provider',
    control_activity: 'Control activity',
    msp_governance_check: 'Material Service Provider governance check',
    register_record: 'Register record',
} as const satisfies Record<string, string>;

export type WorkloadRecordType = keyof typeof WORKLOAD_RECORD_TYPE_LABELS;

export type UnifiedWorkloadStatus = keyof typeof UNIFIED_WORKLOAD_STATUSES;
export type UnifiedWorkloadStatusValue = (typeof UNIFIED_WORKLOAD_STATUSES)[keyof typeof UNIFIED_WORKLOAD_STATUSES];
export type UnifiedCompletionStatus = keyof typeof UNIFIED_COMPLETION_STATUSES;
export type CombinedWorkloadStatus = keyof typeof COMBINED_WORKLOAD_STATUSES;
export type WorkItemType = (typeof WORK_ITEM_TYPES)[keyof typeof WORK_ITEM_TYPES];
